import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

/**
 * Base promise-state primitives, relocated verbatim from the former
 * `~/legacy/core/utils`. These are the side-effect-free building blocks; the
 * Lucian wrappers in `~/shared/hooks/useMutation` and `~/shared/hooks/usePromise` layer
 * notification side effects (`useResolvedPromise`) on top of them.
 */

export interface PromiseStateConfig {
  initialLoading?: boolean
}

const DEFAULT_PROMISE_CONFIG = {
  initialLoading: false,
}

function usePromiseState<S, E = unknown>(config: PromiseStateConfig = DEFAULT_PROMISE_CONFIG) {
  const [error, setError] = useState<null | E>(null)
  const [loading, setLoading] = useState<boolean>(config.initialLoading ?? false)
  const [result, setResult] = useState<S>()
  return { error, setError, loading, setLoading, result, setResult }
}

export function useMutation<P extends unknown[], S, E = unknown>(
  getPromise: (...args: P) => Promise<S>,
  config: PromiseStateConfig = DEFAULT_PROMISE_CONFIG,
) {
  const { error, setError, loading, setLoading, result, setResult } = usePromiseState<S, E>(config)

  // `handler` keeps one identity for the component's lifetime, so an effect can list it as a
  // dependency. The trade-off: it calls the newest `getPromise`, not the one from the render it
  // was created in. Synced in the layout phase, before any consumer effect can call the handler.
  const getPromiseRef = useRef(getPromise)
  useLayoutEffect(() => {
    getPromiseRef.current = getPromise
  }, [getPromise])

  const handler = useCallback(
    async (...args: P) => {
      try {
        setLoading(true)
        setError(null)
        const data = await getPromiseRef.current(...args)
        setResult(data)
        return data
      } catch (e) {
        setError(e as E)
      } finally {
        setLoading(false)
      }
    },
    [setError, setLoading, setResult],
  )

  // `handler` returns the promise for callers that sequence on it. `run` is the same call for
  // places that cannot — a JSX handler, a timer — and is typed void because every rejection has
  // already been caught into `error` above, so there is nothing left for the caller to discard.
  const run = useCallback(
    (...args: P) => {
      void handler(...args)
    },
    [handler],
  )

  return { error, handler, run, loading, result }
}

export function usePromise<P, E = unknown>(
  getPromise: () => Promise<P>,
  config: PromiseStateConfig = { initialLoading: true },
) {
  const { error, handler, loading, result } = useMutation<[], P, E>(getPromise, config)
  useEffect(() => {
    void handler()
  }, [handler])
  return { error, loading, result }
}
